import { useState, useEffect, memo } from 'react';
import {
  useTranslation,
  AlertMessage,
  Theme,
  TextLink,
  TextLinkUnderlineStyle,
  useSession,
  TextWeight,
  useNextgenRouter,
} from '@nextgen-web-framework/all';
import {
  RenewalFlag,
  Cart,
  CartConstantsEnum,
  CartEntryDetails,
  CartEventsEnum,
  InitialCartState,
  OrderType,
  PublishEvent,
  cartApi,
  switchToRegularCart,
  updateCart,
  getAccountProductType,
} from '@commerce-cart-webcomponents/sdk';
import { type AppState, DateFormats, StorageService, formatDate, formatPrice } from '@nextgen-web-framework/core';
import { useSelector, useDispatch } from 'react-redux';
import { AccountType } from '@commerce-cart-webcomponents/sdk/services/models/AccountType';
import { AccountProcessType } from '@commerce-cart-webcomponents/sdk/services/models/AccountProcessType';
import { AccountDetailRenewalLifecycleEnum } from '@commerce-profile-webcomponents/sdk';
import { useRenewalStatus } from '@commerce-profile-webcomponents/ui';
import { AccountProductType } from '@commerce-cart-webcomponents/sdk/services/models/AccountProductType';
import { ModalWrapper } from '../../../common';
import styles from './AboMembershipRenewal.module.scss';

export type AboMembershipRenewalType = {
  hasAccountProcess: boolean;
  cartEntries?: CartEntryDetails[];
};

export type RenewFlagsType = {
  showRenewalNudge: boolean;
};

export const AboMembershipRenewal = memo(({ hasAccountProcess, cartEntries }: AboMembershipRenewalType) => {
  const { isABO, isMember, isAnonymous } = useSession();
  const router = useNextgenRouter();
  const [showRenewNowModal, setShowRenewNowModal] = useState(false);
  const commerceCartState = useSelector((state: AppState) => state.commerceCart);
  const { cart }: InitialCartState = commerceCartState;
  const [renewFlags, setRenewFlags] = useState<RenewFlagsType>({ showRenewalNudge: false });
  const t = useTranslation();
  const dispatch = useDispatch();
  const renewalFlag = cart?.amwayUser?.renewalFlag;
  const expiryDate = cart?.amwayUser?.expirationDate
    ? formatDate(String(cart?.amwayUser?.expirationDate), DateFormats.SHORT_DATE, router?.params?.locale as string)
    : '';

  const feeProduct = getAccountProductType(cartEntries ?? [], AccountProductType.Fee);

  const lateFeeProduct = getAccountProductType(cartEntries ?? [], AccountProductType.LateFee);

  const hasRenewalFlag =
    (isABO() || isMember()) &&
    renewalFlag &&
    !hasAccountProcess &&
    cart?.amwayUser?.renewalLifeCycle === AccountDetailRenewalLifecycleEnum.None;

  const { getRenewalNudgeStatus } = useRenewalStatus();

  useEffect(() => {
    const fetchNudgeStatus = async () => {
      const data = await getRenewalNudgeStatus();
      setRenewFlags(data);
    };
    fetchNudgeStatus();
  }, []);

  const { showRenewalNudge } = renewFlags || {};

  const mustRenewAlert = hasRenewalFlag && renewalFlag === RenewalFlag.MustRenew && showRenewalNudge;

  const showRenewNowAlert = hasRenewalFlag && renewalFlag === RenewalFlag.CanRenew && showRenewalNudge;

  useEffect(() => {
    if (mustRenewAlert) {
      renewAccount();
    }
  }, [renewalFlag, showRenewalNudge]);

  useEffect(() => {
    if (
      !isAnonymous() &&
      typeof cart.accountProcess === 'object' &&
      (cart.accountProcess.type === AccountProcessType.Renewal ||
        cart.accountProcess.type === AccountProcessType.Upgrade ||
        cart.accountProcess.type === AccountProcessType.Downgrade) &&
      cart.amwayUser?.renewalFlag &&
      (![RenewalFlag.CanRenew as string, RenewalFlag.MustRenew as string].includes(
        cart.amwayUser.renewalFlag as string,
      ) ||
        (cart.amwayUser?.renewalLifeCycle &&
          cart?.amwayUser?.renewalLifeCycle !== AccountDetailRenewalLifecycleEnum.None))
    ) {
      const cartId = StorageService.getLocalStorage().retrieve(CartConstantsEnum.CartId);
      cartApi
        .updateCart(cartId, cartId, {
          removeAccountProcess: true,
          orderType: OrderType.Regular,
          version: +StorageService.getLocalStorage().retrieve(CartConstantsEnum.CartReqVersion),
        })
        .then((res) => {
          dispatch(updateCart(res as Cart));
          PublishEvent({
            cartEvent: CartEventsEnum.UpdateMiniCartIcon,
            cart: res as Cart,
          });
        });
    }
  }, [cart]);

  const isRegularCart = cart.orderType === OrderType.Regular;

  const renewAccount = async () => {
    const cartId = StorageService.getLocalStorage().retrieve(CartConstantsEnum.CartId);
    const res = (await cartApi.updateCart(cartId, cartId, {
      accountProcess: {
        type: AccountProcessType.Renewal,
        businessNature: isABO() ? AccountType.BusinessOwner : AccountType.Member,
      },
      orderType: OrderType.Regular,
      version: +StorageService.getLocalStorage().retrieve(CartConstantsEnum.CartReqVersion),
    })) as Cart;
    dispatch(updateCart(res as Cart));
  };

  const orderForRegularCart = async () => {
    const res = await switchToRegularCart(cart);
    dispatch(updateCart(res as Cart));
    renewAccount();
    setShowRenewNowModal(false);
  };

  return (
    <>
      {showRenewNowModal && (
        <ModalWrapper
          headLineText={t('cart.aboMembershipRenewal.renewNow')}
          description={t('cart.aboMembershipRenewal.renewNowDesc')}
          primaryButtonLabel={t('cart.cartContainer.continueText')}
          secondaryButtonLabel={t('cart.clearCart.secondaryButton')}
          onSecondarySuccess={() => setShowRenewNowModal(false)}
          onPrimarySuccess={orderForRegularCart}
        />
      )}
      {cart?.amwayUser?.renewalLifeCycle === AccountDetailRenewalLifecycleEnum.Processing && (
        <AlertMessage hasIcon={false} theme={Theme.PURPLE} className={styles['alert-message-container']}>
          {t('cart.aboMembershipRenewal.renewalInProcess')}
        </AlertMessage>
      )}
      {showRenewNowAlert && (
        <AlertMessage hasIcon={false} theme={Theme.PURPLE} className={styles['alert-message-container']}>
          <div className={styles['alert-message-banner']}>
            <div className={styles['alert-message-banner-text']}>
              {`${t('cart.aboMembershipRenewal.expirationText', {
                userType: isABO() ? t('cart.aboMembershipRenewal.abo') : t('cart.aboMembershipRenewal.member'),
              })} ${expiryDate}`}
            </div>
            <div className={styles['alert-message-banner-textlink']}>
              <TextLink
                onClick={isRegularCart ? renewAccount : () => setShowRenewNowModal(true)}
                underline={TextLinkUnderlineStyle.FIXED}
                text={t('cart.aboMembershipRenewal.renewNow')}
                theme={Theme.WHITE}
              />
            </div>
          </div>
        </AlertMessage>
      )}

      {feeProduct && cart.accountProcess?.type === AccountProcessType.Renewal && (
        <AlertMessage
          content={{
            heading: `${
              isABO() ? t('cart.aboMembershipRenewal.aboRenewalFees') : t('cart.aboMembershipRenewal.memberRenewalFees')
            } - ${formatPrice(feeProduct.totalPrice?.value ?? 0)}`,
          }}
          hasIcon
          icon={'address-card'}
          onClick={() => void 0}
          textWeight={TextWeight.BOLD}
          theme={Theme.ACTION}
        />
      )}

      {lateFeeProduct && cart.accountProcess?.type === AccountProcessType.Renewal && (
        <AlertMessage
          content={{
            heading: `${t('cart.aboMembershipRenewal.lateRenewalFees')} - ${formatPrice(
              lateFeeProduct.totalPrice?.value ?? 0,
            )}`,
          }}
          hasIcon
          icon={'address-card'}
          onClick={() => void 0}
          textWeight={TextWeight.BOLD}
          theme={Theme.WARNING}
        />
      )}
    </>
  );
});

AboMembershipRenewal.displayName = 'AboMembershipRenewal';
